import { getToken } from './authService';

const API_BASE = process.env.REACT_APP_AUTH_API
  || (process.env.NODE_ENV === 'production' ? '/api/linkedin' : 'http://localhost:8080/api/linkedin');

function linkedinUrl(path: string) {
  const base = API_BASE.replace(/\/auth$/, '/linkedin').replace(/\/linkedin$/, '/linkedin');
  return base.endsWith('/linkedin') ? `${base}/${path}` : `${API_BASE}/${path}`;
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token
    ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    : { 'Content-Type': 'application/json' };
}

export interface LinkedInWeek {
  id?: number;
  week_date: string;
  publicaciones: number | null;
  comentarios: number | null;
  impresiones: number | null;
  visualizaciones: number | null;
  seguidores: number | null;
  busquedas: number | null;
  contactos_reclutador: number | null;
  entrevistas: number | null;
  ofertas: number | null;
  ssi_total: number | null;
  ssi_marca: number | null;
  ssi_personas: number | null;
  ssi_interactuar: number | null;
  ssi_relaciones: number | null;
  posicionamiento: string | null;
}

export interface Postulacion {
  id?: number;
  empresa: string;
  puesto: string;
  fecha: string;
  origen: 'inbound' | 'outbound';
  fase: number;
  notas?: string | null;
}

export async function loadMetrics(userId?: number): Promise<LinkedInWeek[]> {
  const token = getToken();
  if (!token) return [];
  try {
    const url = userId ? `${linkedinUrl('metrics.php')}?user_id=${userId}` : linkedinUrl('metrics.php');
    const res = await fetch(url, { headers: authHeaders() });
    if (!res.ok) return [];
    const data = await res.json();
    return data.metrics ?? [];
  } catch {
    return [];
  }
}

export async function saveMetrics(week: Partial<LinkedInWeek> & { week_date: string }): Promise<boolean> {
  const token = getToken();
  if (!token) return false;
  try {
    const res = await fetch(linkedinUrl('metrics.php'), {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(week),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function deleteMetric(id: number): Promise<boolean> {
  const token = getToken();
  if (!token) return false;
  try {
    const res = await fetch(`${linkedinUrl('metrics.php')}?id=${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function loadPostulaciones(userId?: number): Promise<Postulacion[]> {
  const token = getToken();
  if (!token) return [];
  try {
    const url = userId ? `${linkedinUrl('postulaciones.php')}?user_id=${userId}` : linkedinUrl('postulaciones.php');
    const res = await fetch(url, { headers: authHeaders() });
    if (!res.ok) return [];
    const data = await res.json();
    return data.postulaciones ?? [];
  } catch {
    return [];
  }
}

export async function savePostulacion(p: Omit<Postulacion, 'id'>): Promise<number | null> {
  const token = getToken();
  if (!token) return null;
  try {
    const res = await fetch(linkedinUrl('postulaciones.php'), {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(p),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.id ?? null;
  } catch {
    return null;
  }
}

export async function updatePostulacion(p: Partial<Postulacion> & { id: number }): Promise<boolean> {
  const token = getToken();
  if (!token) return false;
  try {
    const res = await fetch(linkedinUrl('postulaciones.php'), {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(p),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function deletePostulacion(id: number): Promise<boolean> {
  const token = getToken();
  if (!token) return false;
  try {
    const res = await fetch(`${linkedinUrl('postulaciones.php')}?id=${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return res.ok;
  } catch {
    return false;
  }
}

// --- Sugerencias ---

export interface Sugerencia {
  id?: number;
  tipo: 'positivo' | 'negativo';
  texto: string;
  created_at?: string;
}

export async function loadSugerencias(): Promise<Sugerencia[]> {
  try {
    const res = await fetch(linkedinUrl('sugerencias.php'), { headers: authHeaders() });
    if (!res.ok) return [];
    return await res.json();
  } catch {
    return [];
  }
}

export async function saveSugerencia(data: { tipo: string; texto: string }): Promise<boolean> {
  try {
    const res = await fetch(linkedinUrl('sugerencias.php'), {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function deleteSugerencia(id: number): Promise<boolean> {
  try {
    const res = await fetch(linkedinUrl('sugerencias.php'), {
      method: 'DELETE',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

// --- Perfil (baseline + milestones) ---

export interface LinkedInPerfil {
  salario_actual?: number | null;
  salario_moneda?: string;
  salario_nuevo?: number | null;
  fecha_primer_post?: string | null;
  fecha_primer_contacto_reclutador?: string | null;
  fecha_primera_entrevista?: string | null;
  fecha_primera_oferta?: string | null;
}

export async function loadPerfil(userId?: number): Promise<LinkedInPerfil> {
  try {
    const url = userId ? `${linkedinUrl('perfil.php')}?user_id=${userId}` : linkedinUrl('perfil.php');
    const res = await fetch(url, { headers: authHeaders() });
    if (!res.ok) return {};
    return await res.json();
  } catch {
    return {};
  }
}

export async function savePerfil(data: Partial<LinkedInPerfil>): Promise<LinkedInPerfil | null> {
  try {
    const res = await fetch(linkedinUrl('perfil.php'), {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}
